import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { AnalyticsService } from 'services';
import { DocumentIcon, AlertIcon, LawyersIcon, UsersIcon } from 'assets/icons';
import { DateRange } from '../hooks/use-date-range';
import { buildDateCategoryAxis } from '../utils/chart-axis';
import { buildGradientFill } from '../utils/chart-style';
import { buildRadialGaugeOptions } from '../utils/radial-gauge';
import SectionHeader from '../components/section-header';
import ChartCard from '../components/chart-card';
import KpiCard from '../components/kpi-card';

interface Props {
  range: DateRange;
}

const RevisionSection = ({ range }: Props) => {
  const [loadingSnapshot, setLoadingSnapshot] = useState(true);
  const [totals, setTotals] = useState({ total: 0, withLawyerSelected: 0, notTaken: 0 });
  const [split, setSplit] = useState({ internal: 0, external: 0 });

  const [loadingHistory, setLoadingHistory] = useState(true);
  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSnapshot(true);
      try {
        const [t, s]: any[] = await Promise.all([
          AnalyticsService.revision.totals({ withoutLoading: true }),
          AnalyticsService.revision.internalVsExternal({ withoutLoading: true }),
        ]);
        if (!cancelled) {
          setTotals(t || { total: 0, withLawyerSelected: 0, notTaken: 0 });
          setSplit(s || { internal: 0, external: 0 });
        }
      } catch (error) {
        console.error('[RevisionSection] Error cargando totales/split', error);
      } finally {
        if (!cancelled) setLoadingSnapshot(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingHistory(true);
      try {
        const res: any = await AnalyticsService.revision.creationHistory({
          ...range,
          withoutLoading: true,
        });
        if (!cancelled) setHistory(res || []);
      } catch (error) {
        console.error('[RevisionSection] Error cargando histórico de solicitudes', error);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range.startDate, range.endDate]);

  const categories = history.map((r) => moment(r.date).format('DD/MM'));
  const withLawyerRate =
    totals.total > 0 ? Math.round((totals.withLawyerSelected / totals.total) * 100) : 0;

  return (
    <section className='analytics-section'>
      <SectionHeader
        title='Revisión documental'
        subtitle='El % con abogado y el desglose interno/externo no dependen del filtro; la gráfica mide solicitudes reales, incluidas las que no consiguieron abogado'
      />
      <div className='grid grid-asym'>
        <ChartCard
          title='Solicitudes reales por día (interno vs. externo)'
          loading={loadingHistory}
          empty={!loadingHistory && history.length === 0}
          type='area'
          height={380}
          series={[
            { name: 'Interno', data: history.map((r) => r.internal) },
            { name: 'Externo (marketplace)', data: history.map((r) => r.external) },
          ]}
          options={{
            chart: { toolbar: { show: false }, zoom: { enabled: false } },
            dataLabels: { enabled: false },
            stroke: { curve: 'smooth', width: 3 },
            xaxis: buildDateCategoryAxis(categories),
            legend: { position: 'top', horizontalAlign: 'right' },
            ...buildGradientFill(['#7f00b2', '#19b6d1']),
          }}
        />
        <div className='side-stack'>
          <KpiCard label='Casos totales' value={totals.total} icon={DocumentIcon} accent='blue' />
          <ChartCard
            title='% con abogado'
            loading={loadingSnapshot}
            empty={false}
            type='radialBar'
            height={180}
            series={[withLawyerRate]}
            options={buildRadialGaugeOptions('#49aa43', 'Con abogado')}
          />
          <div className='grid grid-kpi'>
            <KpiCard label='Sin tomar' value={totals.notTaken} icon={AlertIcon} accent='orange' />
            <KpiCard label='Interno' value={split.internal} icon={LawyersIcon} accent='purple' />
            <KpiCard label='Externo' value={split.external} icon={UsersIcon} accent='cyan' />
          </div>
        </div>
      </div>
    </section>
  );
};

export default RevisionSection;
